import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { PropertyService } from '../../services/property.service';
import { OrganizationService } from '../../services/organization.service';
import { NavigationContextService } from '../../services/navigation-context.service';
import { DEFAULT_EXPENSE_CATEGORIES } from '../../constants/expense-categories';

@Component({
  selector: 'app-property-expenses',
  templateUrl: './property-expenses.component.html',
  styleUrls: ['./property-expenses.component.scss']
})
export class PropertyExpensesComponent implements OnInit, AfterViewInit {
  property: any;
  loading = false;
  displayedColumns = ['date', 'category', 'amount', 'receipt', 'actions'];
  dataSource = new MatTableDataSource<any>();
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  
  // Pagination properties
  totalItems = 0;
  currentPage = 1;
  pageSize = 10;
  
  // Filter properties
  searchText: string = '';
  selectedCategory: string | null = null;
  selectedDateFilter: string = 'all'; // 'all', 'thisMonth', 'lastMonth', 'custom'
  customStartDate: string = '';
  customEndDate: string = '';
  allExpenses: any[] = [];
  
  expenseCategories: string[] = [...DEFAULT_EXPENSE_CATEGORIES];

  constructor(private propertyService: PropertyService,
              private organizationService: OrganizationService,
              private route: ActivatedRoute,
              private router: Router,
              private navContext: NavigationContextService) { }

  ngOnInit() {
    this.loadExpenseCategories();
    const propertyId: string | null = this.route.snapshot.queryParamMap.get('propertyId');

    if (!propertyId) {
      // Property expenses requires a property; fall back to org-wide expenses.
      this.router.navigate(['/organization-expenses']);
      return;
    }

    this.propertyService.getProperties().then(props => {
      this.property = props.find(p => p.id === propertyId);
      this.loadAllExpenses();
    });
  }
  
  loadExpenseCategories() {
    this.organizationService.getExpenseCategories().then(categories => {
      if (Array.isArray(categories) && categories.length > 0) {
        this.expenseCategories = categories;
      }
    }).catch(() => {
      // Keep the default list on failure
    });
  }

  loadAllExpenses() {
    if (!this.property || !this.property.id) {
      return;
    }
    
    this.loading = true;
    this.organizationService.getAllExpenses(this.currentPage, this.pageSize, this.property.id).then((response: any) => {
      this.loading = false;
      
      // Handle paginated response
      if (response && response.data && Array.isArray(response.data)) {
        // IMPORTANT: Replace allExpenses with ONLY the current page's data
        // Ensure receiptUrl field is properly mapped (handle both receipt_url and receiptUrl)
        this.allExpenses = response.data.map(exp => {
          // Preserve statementId (handle both camelCase and snake_case)
          if (exp.statement_id && !exp.statementId) {
            exp.statementId = exp.statement_id;
          }
          // Ensure receiptUrl is set (handle snake_case from database)
          if (!exp.receiptUrl && exp.receipt_url) {
            exp.receiptUrl = exp.receipt_url;
          }
          // Debug: log statementId for first expense
          if (this.allExpenses.length === 0 && exp.statementId) {
            console.log('[PropertyExpenses] Found expense with statementId:', exp.id, exp.statementId);
          }
          return exp;
        });
        this.totalItems = response.total || 0;
        this.currentPage = response.page || 1;
        this.pageSize = response.pageSize || 10;
        console.log(`[PropertyExpenses] Loaded page ${this.currentPage}, showing ${this.allExpenses.length} of ${this.totalItems} total expenses`);
        if (this.allExpenses.length > 0) {
          console.log(`[PropertyExpenses] Sample expense receiptUrl:`, this.allExpenses[0].receiptUrl || 'none');
        }
      } else {
        // Fallback for non-paginated response
        this.allExpenses = Array.isArray(response) ? response.map(exp => {
          if (!exp.receiptUrl && exp.receipt_url) {
            exp.receiptUrl = exp.receipt_url;
          }
          return exp;
        }) : [];
        this.totalItems = this.allExpenses.length;
        console.warn('[PropertyExpenses] Received non-paginated response');
      }
      
      // Apply client-side search and category filters on current page data
      this.applyAllFilters();
    }).catch((error) => {
      console.error('Error loading expenses:', error);
      this.loading = false;
      this.allExpenses = [];
      this.totalItems = 0;
    });
  }
  
  onPageChange(event: any) {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadAllExpenses();
  }

  deleteExpense(expense) {
    if (this.hasStatementId(expense)) {
      alert('Cannot delete expense created from a statement. Remove the statement instead.');
      return;
    }
    if (!confirm('Are you sure you want to delete this expense?')) {
      return;
    }
    this.propertyService.deleteExpense(this.property.id, expense).then(() => {
      this.loadAllExpenses();
    }).catch((error) => {
      console.error('Error deleting expense:', error);
      const errorMessage = (error && error.error && error.error.error) ? error.error.error : (error.message || 'Unknown error');
      if (errorMessage.includes('statement')) {
        alert('Cannot delete expense created from a statement. Remove the statement instead.');
      } else {
        alert('Failed to delete expense. Please try again.');
      }
    });
  }

  hasStatementId(item: any): boolean {
    if (!item) return false;
    // Check both camelCase and snake_case, and also check for truthy values (not null, undefined, or empty string)
    const statementId = item.statementId || item.statement_id;
    const hasId = !!(statementId && statementId !== null && statementId !== undefined && statementId !== '');
    
    // Also check if receiptUrl points to a statement file (fallback for expenses created before statementId was properly set)
    const receiptUrl = item.receiptUrl || item.receipt_url || '';
    const hasStatementReceipt = receiptUrl.includes('/statements/');
    
    return hasId || hasStatementReceipt;
  }

  refreshExpense(expense) {
    if (this.hasStatementId(expense)) {
      return;
    }
    this.propertyService.refreshExpense(this.property.id, expense).then(() => {
      this.loadAllExpenses();
    });
  }

  refreshAll() {
    this.propertyService.refreshExpenses(this.property.id).then(() => {
      this.loadAllExpenses();
    });
  }

  addReceipt(event) {
    const file: File = event.target.files[0];
    if (!file) {
      return;
    }
    this.loading = true;
    const category = 'OTHER_EXPENSES';
    const amount = 0;
    const useAi = confirm('Allow AI to categorize this receipt? Selecting "Cancel" keeps the image private.');
    this.propertyService.uploadExpense(this.property.id, file, category, amount, useAi).then(() => {
      this.loadAllExpenses();
    }).catch(() => {
      this.loading = false;
    });
    // clear input so same file can be uploaded again if needed
    event.target.value = '';
  }

  viewReceipt(exp) {
    if (!exp || !exp.id) {
      return;
    }
    this.router.navigate(['expense-receipt'], {
      queryParams: { propertyId: this.property.id, expenseId: exp.id }
    });
  }


  // Removed updateDataSource - now using loadAllExpenses() for server-side pagination

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchText = filterValue.trim().toLowerCase();
    this.applyAllFilters();
  }

  onCategoryFilterChange(category: string | null) {
    this.selectedCategory = category;
    // Category filter is client-side only for now
    this.applyAllFilters();
  }

  onDateFilterChange(filterType: string) {
    this.selectedDateFilter = filterType;
    if (filterType === 'thisMonth') {
      const now = new Date();
      this.customStartDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      this.customEndDate = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
    } else if (filterType === 'lastMonth') {
      const now = new Date();
      this.customStartDate = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split('T')[0];
      this.customEndDate = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0];
    } else if (filterType === 'all') {
      this.customStartDate = '';
      this.customEndDate = '';
    }
    this.currentPage = 1; // Reset to first page when filter changes
    this.loadAllExpenses();
  }

  onCustomDateChange() {
    if (this.customStartDate && this.customEndDate) {
      this.selectedDateFilter = 'custom';
    }
    this.currentPage = 1; // Reset to first page when filter changes
    this.loadAllExpenses();
  }

  applyAllFilters() {
    // With server-side pagination, date filters are handled server-side
    // Only apply category and search text filters client-side on the current page
    let filtered = [...this.allExpenses];

    // Filter by category (client-side on current page only)
    if (this.selectedCategory) {
      filtered = filtered.filter(exp => exp.category === this.selectedCategory);
    }

    // Filter by search text (client-side on current page only)
    if (this.searchText) {
      filtered = filtered.filter(exp => {
        const searchLower = this.searchText.toLowerCase();
        const category = exp.category ? exp.category.toLowerCase() : '';
        const amount = exp.amount ? String(exp.amount).toLowerCase() : '';
        const date = exp.date ? new Date(exp.date).toLocaleDateString().toLowerCase() : '';
        
        return category.includes(searchLower) ||
               amount.includes(searchLower) ||
               date.includes(searchLower);
      });
    }

    this.dataSource.data = filtered;
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
    // Explicitly ensure paginator is NOT connected to dataSource for server-side pagination
    if (this.dataSource.paginator) {
      this.dataSource.paginator = null;
    }
  }

  ngAfterViewInit() {
    if (this.sort && this.dataSource) {
      this.dataSource.sort = this.sort;
    }
    // Explicitly ensure paginator is NOT connected to dataSource
    if (this.dataSource.paginator) {
      this.dataSource.paginator = null;
    }
    if (this.paginator) {
      // For server-side pagination, we handle pagination events manually
      this.paginator.page.subscribe(event => {
        this.onPageChange(event);
      });
    }
  }

  clearFilters() {
    this.selectedCategory = null;
    this.searchText = '';
    this.selectedDateFilter = 'all';
    this.customStartDate = '';
    this.customEndDate = '';
    this.currentPage = 1; // Reset to first page when clearing filters
    this.loadAllExpenses();
  }

  close() {
    if (this.property && this.property.id) {
      this.navContext.goToPropertyHub(this.property.id);
      return;
    }
    this.navContext.goToDashboard();
  }
}
